import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import { USER_ROLES } from "../../const";
import { useSubscription } from "../../hooks/useSubscription";
import { useDismissedBanner } from "../../hooks/useDismissedBanner";
import { useBillingOverview } from "../../features/billing/hooks/useBillingOverview";
import FreePlanToastContent from "./FreePlanToastContent";

const FREE_PLAN_TOAST_ID = "free-plan-banner";

const FreePlanBanner = () => {
  const { user } = useAuth();
  const { isFreePlan, unlimitedAccess, loading } = useSubscription();
  const { data: overview, isLoading: overviewLoading } = useBillingOverview();
  const { dismissed, dismiss } = useDismissedBanner("freePlanBanner", user?.id);
  const navigate = useNavigate();
  const shownRef = useRef(false);

  const hasCredits = (overview?.balanceCents ?? 0) > 0;
  // Super admins run the platform; they have no plan of their own to upsell.
  const isSuperAdmin = user?.role?.toLowerCase?.() === USER_ROLES.SUPER_ADMIN;

  useEffect(() => {
    if (shownRef.current) return;
    if (loading || overviewLoading) return;
    if (isSuperAdmin) return;
    if (!isFreePlan || unlimitedAccess || hasCredits || dismissed) return;

    shownRef.current = true;

    const openBilling = () => {
      toast.dismiss(FREE_PLAN_TOAST_ID);
      navigate("/billing");
    };

    toast.info(<FreePlanToastContent onOpenBilling={openBilling} />, {
      toastId: FREE_PLAN_TOAST_ID,
      position: "top-center",
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      onClose: (reason) => {
        if (reason === true) dismiss();
      },
    });
  }, [
    isSuperAdmin,
    loading,
    overviewLoading,
    isFreePlan,
    unlimitedAccess,
    hasCredits,
    dismissed,
    dismiss,
    navigate,
  ]);

  useEffect(() => () => toast.dismiss(FREE_PLAN_TOAST_ID), []);

  return null;
};

export default FreePlanBanner;
